import { useState, useEffect } from 'react'

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>
}

interface PWAHook {
  isInstallable: boolean
  isInstalled: boolean
  isOnline: boolean
  promptInstall: () => Promise<void>
  clearInstallPrompt: () => void
  debugInfo: {
    hasServiceWorker: boolean
    hasManifest: boolean
    isHttps: boolean
    userAgent: string
    deferredPromptReceived: boolean
  }
}

export const usePWA = (): PWAHook => {
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null)
  const [isInstalled, setIsInstalled] = useState(false)
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [debugInfo, setDebugInfo] = useState({
    hasServiceWorker: 'serviceWorker' in navigator,
    hasManifest: false,
    isHttps: window.location.protocol === 'https:' || window.location.hostname === 'localhost',
    userAgent: navigator.userAgent,
    deferredPromptReceived: false
  })

  useEffect(() => {
    // Check if manifest is properly linked
    const checkManifest = () => {
      const manifestLink = document.querySelector('link[rel="manifest"]')
      const hasManifest = !!manifestLink
      console.log('PWA Debug - Manifest check:', { hasManifest, manifestLink })
      
      setDebugInfo(prev => ({ ...prev, hasManifest }))
    }

    checkManifest()

    // Check if app is already installed
    const checkIfInstalled = () => {
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches
      const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream
      const isIOSInstalled = isIOS && (navigator as any).standalone
      
      const installed = isStandalone || isIOSInstalled
      console.log('PWA Debug - Installation check:', { 
        isStandalone, 
        isIOS, 
        isIOSInstalled, 
        installed,
        userAgent: navigator.userAgent
      })
      
      setIsInstalled(installed)
    }

    checkIfInstalled()

    // Listen for beforeinstallprompt event
    const handleBeforeInstallPrompt = (e: Event) => {
      console.log('PWA Debug - beforeinstallprompt event received:', e)
      e.preventDefault()
      setDeferredPrompt(e as BeforeInstallPromptEvent)
      setDebugInfo(prev => ({ ...prev, deferredPromptReceived: true }))
    }

    // Listen for app installed event
    const handleAppInstalled = () => {
      console.log('PWA Debug - App installed event received')
      setIsInstalled(true)
      setDeferredPrompt(null)
    }

    // Listen for online/offline status
    const handleOnline = () => {
      console.log('PWA Debug - Online')
      setIsOnline(true)
    }
    
    const handleOffline = () => {
      console.log('PWA Debug - Offline')
      setIsOnline(false)
    }

    // Log initial PWA state
    console.log('PWA Debug - Initial state:', {
      protocol: window.location.protocol,
      hostname: window.location.hostname,
      hasServiceWorker: 'serviceWorker' in navigator,
      userAgent: navigator.userAgent,
      isStandalone: window.matchMedia('(display-mode: standalone)').matches,
      navigator: {
        standalone: (navigator as any).standalone,
        onLine: navigator.onLine
      }
    })

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)
    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    // Additional check for PWA criteria after a short delay
    const timeoutId = setTimeout(() => {
      console.log('PWA Debug - Delayed criteria check:', {
        serviceWorkerRegistrations: navigator.serviceWorker?.getRegistrations?.(),
        manifestPresent: !!document.querySelector('link[rel="manifest"]'),
        isSecureContext: window.isSecureContext,
        deferredPromptReceived: !!deferredPrompt
      })
    }, 2000)

    return () => {
      clearTimeout(timeoutId)
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const promptInstall = async (): Promise<void> => {
    if (!deferredPrompt) {
      console.error('PWA Debug - No install prompt available. Debug info:', debugInfo)
      throw new Error('No install prompt available')
    }

    try {
      console.log('PWA Debug - Showing install prompt')
      await deferredPrompt.prompt()
      const choiceResult = await deferredPrompt.userChoice
      
      console.log('PWA Debug - User choice:', choiceResult.outcome)
      
      if (choiceResult.outcome === 'accepted') {
        console.log('User accepted the install prompt')
      } else {
        console.log('User dismissed the install prompt')
      }
      
      setDeferredPrompt(null)
    } catch (error) {
      console.error('PWA Debug - Error showing install prompt:', error)
      throw error
    }
  }

  const clearInstallPrompt = (): void => {
    console.log('PWA Debug - Clearing install prompt')
    setDeferredPrompt(null)
  }

  return {
    isInstallable: !!deferredPrompt,
    isInstalled,
    isOnline,
    promptInstall,
    clearInstallPrompt,
    debugInfo: {
      ...debugInfo,
      deferredPromptReceived: !!deferredPrompt
    }
  }
} 